import { slippageTypes } from '../data';

const typeAccent: Record<string, string> = {
  negative: 'accent',
  positive: 'primary',
  neutral: 'secondary',
};

export default function SlippageTypesSection() {
  return (
    <section className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[640px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Slippage is not always bad
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Negative slippage costs money. Positive slippage saves money. Zero slippage means you got exactly what you clicked.
          </p>
        </div>

        {/* Three-column comparison */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0">
          {slippageTypes.map((t, i) => {
            const tok = typeAccent[t.direction];
            const isNeg = t.direction === 'negative';
            const isPos = t.direction === 'positive';
            const isNeutral = t.direction === 'neutral';

            return (
              <div
                key={t.type}
                className={`relative flex flex-col px-6 md:px-8 py-0 pb-10 md:pb-0 ${
                  i < 2 ? 'border-b md:border-b-0 md:border-r border-background-200' : ''
                }`}
              >
                {/* Subtle top accent line */}
                <div className={`w-10 h-[3px] rounded-full mb-6 ${
                  isNeg ? 'bg-accent-500' : isPos ? 'bg-primary-500' : 'bg-secondary-500'
                }`} />

                {/* Type label */}
                <span className={`text-[12px] font-bold uppercase tracking-[0.12em] mb-2 ${
                  isNeg ? 'text-accent-600' : isPos ? 'text-primary-600' : 'text-secondary-600'
                }`}>
                  {t.type}
                </span>

                {/* Tagline */}
                <p className="text-[21px] md:text-[24px] font-[750] text-foreground-950 leading-[1.2] mb-5 tracking-tight">
                  {isNeg && 'You pay more'}
                  {isPos && 'You pay less'}
                  {isNeutral && 'Perfect match'}
                </p>

                {/* Description */}
                <p className="text-[14px] leading-[1.65] text-foreground-600 mb-7 flex-1">
                  {t.desc}
                </p>

                {/* Price comparison */}
                <div className="mb-1.5">
                  <span className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wide">Requested</span>
                </div>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-[30px] md:text-[34px] font-[800] text-foreground-950 leading-none tabular-nums tracking-tight">
                    {t.requestedPrice}
                  </span>
                  <span className="text-[18px] text-foreground-400 font-light">
                    {isNeutral ? '=' : '→'}
                  </span>
                  <span className={`text-[30px] md:text-[34px] font-[800] leading-none tabular-nums tracking-tight ${
                    isNeutral ? 'text-foreground-950' : isNeg ? 'text-accent-600' : 'text-primary-600'
                  }`}>
                    {t.executedPrice}
                  </span>
                </div>
                <div className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wide mb-4">
                  Executed
                </div>

                {/* Cost */}
                <div className="flex items-center gap-3 mb-2">
                  <span className={`text-[28px] md:text-[32px] font-[800] leading-none tracking-tight ${
                    isNeg ? 'text-accent-600' : isPos ? 'text-primary-600' : 'text-secondary-600'
                  }`}>
                    {isNeg ? `−${t.pipDiff}` : isPos ? `+${t.pipDiff}` : '0'}
                  </span>
                  <span className="text-[13px] text-foreground-500 leading-tight">
                    pips
                    <br />
                    {isNeg ? `−$${t.costPerLot}/lot` : isPos ? `+$${t.costPerLot}/lot` : '$0'}
                  </span>
                </div>

                {/* Frequency */}
                <p className="text-[12px] text-foreground-500 mt-3 pt-4 border-t border-background-200/70">
                  <strong className="text-foreground-700">{t.frequencyPercent}%</strong> of all slippage events
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}